import { importModule } from './import-module'

export type SolidTemplateEntry = {
  // Mounts the template into `el` with the host-supplied request context and
  // returns a disposer that unmounts it.
  mount: (el: HTMLElement, context?: object) => () => void
}

const loadedEntries = new Map<string, Promise<SolidTemplateEntry>>()

export const loadTemplateEntry = (
  entryUrl: string
): Promise<SolidTemplateEntry> => {
  const existing = loadedEntries.get(entryUrl)
  if (existing) return existing

  const loaded = loadEntry(entryUrl).catch((error) => {
    loadedEntries.delete(entryUrl)
    throw error
  })

  loadedEntries.set(entryUrl, loaded)

  return loaded
}

// CONTRACT — the entry module is built by @solid/sdk's registerTemplateEntry
// and MUST export `mount`
// (silverfin-solid: packages/sdk/src/register-template-entry.tsx).
const loadEntry = async (entryUrl: string): Promise<SolidTemplateEntry> => {
  await injectModuleScript(entryUrl)

  const mod = await importModule(entryUrl)
  const { mount } = (mod ?? {}) as Partial<SolidTemplateEntry>

  if (typeof mount !== 'function') {
    throw new Error(`Solid template entry does not export mount: ${entryUrl}`)
  }

  return {
    mount: (el: HTMLElement, context?: object): (() => void) => {
      const dispose = mount(el, context)

      if (typeof dispose !== 'function') {
        throw new Error(
          `Solid template mount did not return a disposer: ${entryUrl}`
        )
      }

      return dispose
    },
  }
}

// `import()` cannot send cross-origin credentials, but the CDN bucket is
// private and needs the signed Cloud-CDN-Cookie. So a
// `<script type="module" crossorigin="use-credentials">` does the credentialed
// fetch (its static-import chunks inherit the same mode), and the `import()`
// afterwards resolves from the browser's module map without a network fetch.
const injectModuleScript = (entryUrl: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script')

    script.type = 'module'
    script.crossOrigin = 'use-credentials'
    script.src = entryUrl

    script.onload = (): void => {
      resolve()
    }
    script.onerror = (): void => {
      reject(new Error(`Failed to load Solid template entry: ${entryUrl}`))
    }

    document.head.appendChild(script)
  })
